import { getRequestConfig, privateApi } from '@services/api';
import { IActiveJobData, ISuggestedJobData } from '@th-types/active-jobs.type';
import {
  IJobListFilters,
  IJobOptionsData,
  IJobSearchType,
  IJobShiftsCounters,
  IJobShiftsResult,
  IJobStatsList,
  NewApplicantsCounterResults,
} from '@th-types/jobs.type';
import IJobDetail, {
  IJobsData,
  JobShareInfo,
  ShiftPositions,
} from '@th-types/job.detail.type';
import IResultResponse from '@th-types/resultResponse.type';
import { JobUpdatableFields } from '@company/types/job.updatable.fields';
import { ActivityDetailList } from '@th-types/daily-activity.type';
import { AxiosError } from 'axios';
import DATE_FORMATS from '@constants/dateFormat';
import { format } from 'date-fns';

export const MAIN_URL = 'jobs';
export const SHIFTS_PAGE_SIZE = 50;

export const fetchJobDetail = async (jobId: number) => {
  const url = `${MAIN_URL}/${jobId}`;
  const result = await privateApi.get<IJobDetail>(url);
  return result.data;
};

export const fetchJobList = async (
  jobFilters: IJobListFilters,
  offset = 0,
  max = 10
) => {
  const params = {
    offset,
    max,
    dateFrom: jobFilters?.dateRange?.startDate,
    jobSearchType: jobFilters.jobSearchType,
    dateTo: jobFilters?.dateRange?.endDate,
    searchFilter: jobFilters.searchFilter,
    hiddenJobs: jobFilters.hiddenJobs,
    newApplicantsOnly:
      jobFilters.newApplicantsOnly &&
      jobFilters.jobSearchType === IJobSearchType.ACTIVE_MEMBER,
  };

  const result = await privateApi.get<IJobsData>(MAIN_URL, { params });
  return result.data;
};

export const fetchJobShiftsList = async (
  jobFilters: IJobListFilters,
  offset = 0,
  max = 100
) => {
  const params = {
    offset,
    max,
    dateFrom: jobFilters?.dateRange?.startDate
      ? format(
          jobFilters.dateRange.startDate,
          DATE_FORMATS.ISO8601_DATE_ONLY_FORMAT
        )
      : '',
    dateTo: jobFilters?.dateRange?.endDate
      ? format(
          jobFilters.dateRange.endDate,
          DATE_FORMATS.ISO8601_DATE_ONLY_FORMAT
        )
      : '',
    jobSearchType: jobFilters.jobSearchType,
    searchFilter: jobFilters.searchFilter,
    openShifts: jobFilters.openShifts,
    onboarding: jobFilters.onboarding,
    noCheckInOut: jobFilters.noCheckInOut,
    missingHours: jobFilters.missingHours,
    toApprove: jobFilters.toApprove,
    markPaid: jobFilters.markPaid,
  };

  const result = await privateApi.get<IJobShiftsResult>(`${MAIN_URL}/shifts`, {
    params,
  });
  return result.data;
};

export const fetchJobShiftsCounters = async (
  jobFilters: IJobListFilters,
  offset = 0,
  max = 10
) => {
  const params = {
    offset,
    max,
    dateFrom: jobFilters?.dateRange?.startDate
      ? format(
          jobFilters.dateRange.startDate,
          DATE_FORMATS.ISO8601_DATE_ONLY_FORMAT
        )
      : '',
    dateTo: jobFilters?.dateRange?.endDate
      ? format(
          jobFilters.dateRange.endDate,
          DATE_FORMATS.ISO8601_DATE_ONLY_FORMAT
        )
      : '',
    jobSearchType: jobFilters.jobSearchType,
    searchFilter: jobFilters.searchFilter,
  };

  const result = await privateApi.get<IJobShiftsCounters>(
    `${MAIN_URL}/shifts/counters`,
    {
      params,
    }
  );
  return result.data;
};

export const handleMissingActivityNoticed = async (shiftPositionId: number) => {
  const result = await privateApi.post<IResultResponse>(
    `${MAIN_URL}/shifts/${shiftPositionId}/missing-activities-noticed`
  );
  return result.data;
};

export const handleMissingHoursNoticed = async (shiftPositionId: number) => {
  const result = await privateApi.post<IResultResponse>(
    `${MAIN_URL}/shifts/${shiftPositionId}/missing-hours-noticed`
  );
  return result.data;
};

export const fetchJobStatsList = async (jobIds: number[]) => {
  const result = await privateApi.post<IJobStatsList>(`${MAIN_URL}/stats`, {
    jobIds,
  });
  return result.data;
};

export const getJobShareLinkInfo = async (jobId: number) => {
  const result = await privateApi.get<JobShareInfo>(
    `${MAIN_URL}/${jobId}/share-info`
  );
  return result.data;
};

export const countNewApplicants = async (jobFilters: IJobListFilters) => {
  const params = {
    dateFrom: jobFilters?.dateRange?.startDate,
    dateTo: jobFilters?.dateRange?.endDate,
    ...jobFilters,
  };

  const result = await privateApi.get<NewApplicantsCounterResults>(
    `${MAIN_URL}/count-new-applicants`,
    { params }
  );
  return result.data;
};

export const getNewApplicants = async (jobId: number) => {
  const result = await privateApi.get(`${MAIN_URL}/${jobId}/new-applicants`);
  return result.data;
};

export const countActiveJobs = async (jobFilters: IJobListFilters) => {
  const params = {
    jobSearchType: jobFilters.jobSearchType,
    period: jobFilters.period,
  };

  const result = await privateApi.get<IResultResponse>(
    `${MAIN_URL}/count-active`,
    { params }
  );
  return result;
};

export const hideJobForMember = async (jobId: number) => {
  const result = await privateApi.post<IResultResponse>(
    `${MAIN_URL}/${jobId}/hide-for-member`
  );
  return result.data;
};

export const fetchJobPositionList = async () => {
  const result = await privateApi.get<IJobOptionsData>('search/options');
  return result.data;
};

export const canDeleteJob = async (jobId: number) => {
  const url = `${MAIN_URL}/${jobId}/validate-edition`;
  const result = await privateApi.get<IResultResponse>(url);
  return result.data;
};

export const deleteJob = async (jobId: number) => {
  const url = `${MAIN_URL}/${jobId}`;
  const result = await privateApi.delete<IResultResponse>(url);
  return result.data;
};

export const patchJob = async (jobId: number, fields: JobUpdatableFields) => {
  const url = `${MAIN_URL}/${jobId}`;
  const result = await privateApi.patch<boolean>(url, fields);
  return result.data;
};

export const refreshBullhornJob = async (
  jobId: number,
  integrationId: number | undefined
) => {
  const url = `${MAIN_URL}/${jobId}/integrations/${integrationId}/refresh`;
  const result = await privateApi.post<boolean>(url);
  return result.data;
};

export const addJobShifts = async (jobId: number, data: any) => {
  const result = await privateApi
    .post<IResultResponse>(`${MAIN_URL}/${jobId}/shifts`, data)
    .catch((error) => {
      console.log(
        `Occurred an error when Trusted Herd tried create a new job shift. jobId: ${jobId}`
      );
      console.log(error);
      return error.response;
    });

  return result.data;
};

export const cancelJobShifts = async (jobId: number, shiftIds: number[]) => {
  const result = await privateApi
    .post<IResultResponse>(`${MAIN_URL}/${jobId}/shifts/cancel`, { shiftIds })
    .catch((error) => {
      console.log(
        `Occurred an error when Trusted Herd tried to cancel assigned shift. jobId: ${jobId}`
      );
      return error.response;
    });
  return result;
};

export const deleteJobShifts = async (jobId: number, shiftIds: number[]) => {
  const result = await privateApi
    .delete<IResultResponse>(`${MAIN_URL}/${jobId}/shifts`, {
      data: { shiftIds },
    })
    .catch((error) => {
      console.log(
        `Occurred an error when Trusted Herd tried to cancel assigned shift. jobId: ${jobId}`
      );
      console.log(error);
      return error.response;
    });

  return result.data;
};

export const updateJobShifts = async (
  jobId: number,
  shiftId: number,
  data: any
) => {
  const result = await privateApi
    .put<IResultResponse>(`${MAIN_URL}/${jobId}/shifts/${shiftId}`, data)
    .catch((error) => {
      console.log(
        `Occurred an error when Trusted Herd tried create a new job shift. jobId: ${jobId}`
      );
      console.log(error);
      return error.response;
    });

  return result.data;
};

export const fetchShiftPositions = async (
  jobId: number,
  offset = 0,
  max = SHIFTS_PAGE_SIZE
) => {
  const result = await privateApi
    .get<ShiftPositions>(
      `${MAIN_URL}/${jobId}/shifts`,
      getRequestConfig(offset, max)
    )
    .catch((error) => {
      console.log(
        `Occurred an error when Trusted Herd tried fetch job shift. jobId: ${jobId}`
      );
      console.log(error);
      return error.response;
    });
  return result.data;
};

export const fetchActivityTrackerList = async (
  jobId: number,
  page = 0,
  max = 200
) => {
  const url = `${MAIN_URL}/${jobId}/activity-tracker`;
  const result = await privateApi.get<ActivityDetailList>(
    url,
    getRequestConfig(page, max)
  );
  return result.data;
};

export const markShiftsPaid = async (jobId: number, shiftIds: number[]) => {
  const result = await privateApi
    .post<IResultResponse>(`${MAIN_URL}/${jobId}/shifts/mark-paid`, {
      shiftIds,
    })
    .catch((error) => {
      console.log(
        `Occurred an error when Trusted Herd tried to mark a shift as paid. jobId: ${jobId} - shifts: ${shiftIds}`
      );
      console.log(error);
      return error.response;
    });

  return result.data;
};

export const fetchActiveJobsByWorker = async (
  workerId: number,
  searchFilter?: string | null
): Promise<IActiveJobData> => {
  const result = await privateApi.get<IActiveJobData>(
    `${MAIN_URL}/worker/${workerId}`,
    {
      params: { jobSearchType: 'ACTIVE_ALL', searchFilter, max: 999 },
    }
  );
  return result.data;
};

export const fetchSuggestedJobs = async (
  searchFilter?: string | null,
  page = 0,
  max = 10,
  sort = 'start',
  order = 'desc'
): Promise<ISuggestedJobData> => {
  const result = await privateApi.get<ISuggestedJobData>(MAIN_URL, {
    params: {
      jobSearchType: 'ALL',
      searchFilter,
      offSet: page * max,
      max,
      sort,
      order,
    },
  });
  return result.data;
};

export const markShiftsUnPaid = async (
  jobId: number,
  shiftIds: number[]
): Promise<IResultResponse> => {
  try {
    const result = await privateApi.post<IResultResponse>(
      `${MAIN_URL}/${jobId}/shifts/mark-unpaid`,
      {
        shiftIds,
      }
    );
    return result.data;
  } catch (error) {
    console.log(
      `Occurred an error when Trusted Herd tried to mark a shift as unpaid. jobId: ${jobId} - shifts: ${shiftIds}`
    );
    console.log(error);
    return (error as AxiosError<IResultResponse>).response
      ?.data as IResultResponse;
  }
};
